import type { UserLinkRO } from "@ub/customer-client"
import type { RolePermissionRO, UserRoleRO } from "@ub/requisition-approval-client"

import { CustomerData } from "@/lib/intershop/interfaces/customer/customer"

/**
 * Aggregates and combines permissions for the provided roles, ensuring no duplicates.
 *
 * @param {UserRoleRO[] | undefined} allRoles - Array of all available roles, including their permissions.
 * @param {string[] | undefined} selectedRoleIDs - Array of role IDs for which permissions are to be aggregated.
 * @return {RolePermissionRO[]} An array of unique permissions for the selected roles.
 */
export function aggregatePermissionsForRolesLocal(
  allRoles: UserRoleRO[] | undefined,
  selectedRoleIDs: string[] | undefined
): RolePermissionRO[] {
  if (!allRoles || !selectedRoleIDs || selectedRoleIDs.length === 0) return [] as NonNullable<UserRoleRO["permissions"]>
  const seen = new Set<string>()
  const perms: RolePermissionRO[] = []
  for (const rid of selectedRoleIDs) {
    const role: UserRoleRO | undefined = allRoles.find((r) => r.roleID === rid)
    if (!(!role || !role.permissions)) {
      for (const p of role.permissions) {
        const key = `${p.permissionID ?? p.permissionDisplayName}`
        if (!seen.has(key)) {
          seen.add(key)
          perms.push(p)
        }
      }
    }
  }
  return perms
}

export function extractUserId(userLink: UserLinkRO | undefined) {
  if (userLink?.uri) {
    const result = userLink.login
    if (result) {
      return result
    }
  }

  return ""
}

export function determineSegments(customerData: CustomerData | undefined) {
  if (customerData && customerData?.customerSegments && customerData?.customerSegments.length > 0) {
    const segmentIds: string[] = customerData?.customerSegments.map((segment) => segment.id)
    return segmentIds
  }

  return []
}

// Currency formatting helper extracted for reuse across user components
export function formatCurrency(money: { value?: number; currency?: string } | undefined): string {
  if (!money) return ""
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: money.currency || "EUR",
  }).format(money.value ?? 0)
}
